import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUp } from "lucide-react";
import { PenaltyScene, goalToScene } from "./PenaltyScene";
import * as sfx from "../../lib/stadiumSound";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const shotFromSwipe = ({ dx, dy, ms, W, H }) => {
  if (dy < H * 0.08) return null;
  const x = ((dx / dy) * (0.5 * H)) / (0.33 * W);
  const len = Math.hypot(dx, dy) / H;
  const power = clamp(Math.hypot(dx, dy) / ms / 4, 0.15, 1);
  return { x: clamp(x, -1.4, 1.4), y: clamp((len - 0.12) / 0.62, 0, 1.25), power: +power.toFixed(2) };
};

const diveFromSwipe = ({ dx, dy }) => (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 25 ? (dx < 0 ? "L" : "R") : "C");

const BANNER = {
  goal: { text: "GOAL!", good: true },
  saved: { text: "SAVED!", good: false },
  miss: { text: "MISSED!", good: false },
};
const SAVE_BANNER = {
  goal: { text: "They scored", good: false },
  saved: { text: "WHAT A SAVE!", good: true },
  miss: { text: "They missed!", good: true },
};

const Reticle = ({ shot }) => {
  const p = goalToScene(shot.x, shot.y);
  return (
    <div data-testid="aim-reticle" className="absolute z-30 w-8 h-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#10B981]  animate-pulse" style={{ left: `${p.left}%`, top: `${p.top}%` }} />
  );
};

// Plays one round: shoot -> dive -> wait -> reveal both kicks. Calls onRevealed when done.
export const KickRound = ({ round, me, them, myIn, onSubmit, onRevealed }) => {
  const [step, setStep] = useState(myIn ? "waiting" : "shoot");
  const [shot, setShot] = useState(null);
  const [hint, setHint] = useState(null);
  const [scene, setScene] = useState({ kit: them.keeper, dive: null, ball: null, flying: false, banner: null });
  const started = useRef(false);

  useEffect(() => {
    if (!myIn) sfx.whistle();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!round.resolved || started.current) return;
    started.current = true;
    const mine = round.resolved[me.role];
    const theirs = round.resolved[them.role];
    const T = (ms, fn) => setTimeout(fn, ms);
    setStep("reveal");
    setScene({ kit: them.keeper, dive: null, ball: null, flying: false, banner: null });
    const land = (res, mineKick) => {
      if (res === "goal") { sfx.net(); sfx.roar(mineKick ? 1 : 0.45); }
      else if (res === "saved") { sfx.parry(); mineKick ? sfx.groan() : sfx.roar(0.9); }
      else mineKick ? sfx.groan() : sfx.roar(0.6);
    };
    T(350, () => { sfx.kick(); setScene((s) => ({ ...s, ball: mine, flying: true, dive: mine.dive })); });
    T(880, () => land(mine.result, true));
    T(950, () => setScene((s) => ({ ...s, banner: BANNER[mine.result] })));
    T(2300, () => setScene({ kit: me.keeper, dive: null, ball: null, flying: false, banner: null }));
    T(2700, () => { sfx.kick(); setScene((s) => ({ ...s, ball: theirs, flying: true, dive: theirs.dive })); });
    T(3230, () => land(theirs.result, false));
    T(3300, () => setScene((s) => ({ ...s, banner: SAVE_BANNER[theirs.result] })));
    T(4700, () => onRevealed(round.n));
  }, [round.resolved]); // eslint-disable-line react-hooks/exhaustive-deps

  const dive = (d) => {
    if (step !== "save") return;
    setScene((s) => ({ ...s, dive: d }));
    setStep("waiting");
    onSubmit(round.n, shot, d);
  };

  const onSwipe = (sw) => {
    if (step === "shoot") {
      const s = shotFromSwipe(sw);
      if (!s) return setHint("Swipe UP towards the goal");
      setHint(null);
      sfx.kick();
      setShot(s);
      setTimeout(() => {
        setStep("save");
        setScene({ kit: me.keeper, dive: null, ball: null, flying: false, banner: null });
      }, 650);
    } else if (step === "save") {
      dive(diveFromSwipe(sw));
    }
  };

  const caption =
    step === "shoot"
      ? hint || `Kick ${round.n}: swipe up to shoot · ${me.taker.name} (${me.taker.rating})`
      : step === "save"
      ? `Save ${them.username}'s kick! Swipe left, right or up to dive`
      : step === "waiting"
      ? `Locked in. Waiting for ${them.username}…`
      : null;

  return (
    <div data-testid={`kick-round-${round.n}`}>
      <PenaltyScene
        keeperKit={scene.kit}
        keeperDive={scene.dive}
        ballTarget={scene.ball}
        ballFlying={scene.flying}
        mode={step === "shoot" ? "shoot" : step === "save" ? "save" : null}
        onSwipe={onSwipe}
        banner={scene.banner}
        caption={caption}
      >
        {step === "shoot" && shot && <Reticle shot={shot} />}
      </PenaltyScene>
      <div data-testid="round-step" data-step={step} className="mt-3 grid grid-cols-3 gap-2">
        {step === "save" &&
          [
            { d: "L", Icon: ArrowLeft, label: "Left" },
            { d: "C", Icon: ArrowUp, label: "Middle" },
            { d: "R", Icon: ArrowRight, label: "Right" },
          ].map(({ d, Icon, label }) => (
            <button key={d} data-testid={`dive-${d}-btn`} onClick={() => dive(d)} className="btn-ghost rounded-xl py-2.5 text-sm font-semibold text-white flex items-center justify-center gap-1.5">
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
      </div>
    </div>
  );
};

export default KickRound;
